import {useState, useEffect} from "react";
import {useNavigate} from 'react-router-dom';
import {onlineProcessesService} from "../../services";
import {useLoader} from "@quadral/quadral-component-common/dist/components/loader/LoaderContext";
import QuadralList from "@quadral/quadral-component-common/dist/components/list";
import QuadralButton from '@quadral/quadral-component-common/dist/components/button';
import QuadralBadge from "@quadral/quadral-component-common/dist/components/badge";
import QuadralTypography from '@quadral/quadral-component-common/dist/components/typography';
import AdvancedFilters from "@quadral/quadral-component-common/dist/components/advancedFilters";

const SURVEY_STATUS_ORDER = [
    'en_cours',
    'planifiee',
    'a_planifier',
    'brouillon',
    'suspendue',
    'terminee',
    'annulee'
];

const SURVEY_STATUS_RANK = SURVEY_STATUS_ORDER.reduce((acc, status, index) => {
    acc[status] = index;
    return acc;
}, {});

const compareText = (a, b) => String(a || '').localeCompare(String(b || ''), 'fr', {sensitivity: 'base'});

const sortSurveysByStatus = (rows = [], direction = 'asc') => rows
    .map((survey) => ({
        ...survey,
        statusRank: SURVEY_STATUS_RANK[survey.status]
    }))
    .sort((a, b) => {
        const multiplier = direction === 'desc' ? -1 : 1;

        if (a.statusRank !== b.statusRank) {
            return (a.statusRank - b.statusRank) * multiplier;
        }
        return compareText(a.name, b.name) * multiplier;
    });

// TODO Utiliser le service des références
const STATUS_LABEL_MAP = {
    'brouillon': 'Brouillon',
    'a_planifier': 'À planifier',
    'planifiee': 'Planifiée',
    'en_cours': 'En cours',
    'suspendue': 'Suspendue',
    'terminee': 'Terminée',
    'annulee': 'Annulée'
};

const STATUS_COLOR_MAP = {
    'brouillon': 'control',
    'a_planifier': 'warning',
    'planifiee': 'yellow',
    'en_cours': 'success',
    'suspendue': 'info',
    'terminee': 'secondary',
    'annulee': 'secondary'
};

const buildDefaultFilters = () => ({
    statusList: ['en_cours']
});

const FILTER_FIELDS = [
    {
        id: 'statusList',
        label: 'Statut',
        type: 'multiselect',
        quick: true,
        options: SURVEY_STATUS_ORDER.map((status) => ({
            value: status,
            label: STATUS_LABEL_MAP[status] || status
        }))
    }
];

const buildColumns = (navigate) => [
    {id: 'name', label: 'Libellé', sortable: false},
    {
        id: 'startDate',
        label: 'Date de début',
        sortable: false,
        render: (row) => row.startDate ? new Date(row.startDate).toLocaleDateString('fr-FR') : '-'
    },
    {
        id: 'endDate',
        label: 'Date de fin',
        sortable: false,
        render: (row) => row.endDate ? new Date(row.endDate).toLocaleDateString('fr-FR') : '-'
    },
    {id: 'ownerCompanyName', label: 'Référence client', sortable: false},
    {
        id: 'statusRank',
        label: 'Statut',
        sortable: true,
        render: (row) => (
            <QuadralBadge
                label={STATUS_LABEL_MAP[row.status] || row.status}
                color={STATUS_COLOR_MAP[row.status] || 'info'}
            />
        )
    },
    {
        id: 'actions',
        label: '',
        render: (row) => (
            <QuadralButton
                label='Ouvrir'
                component="a"
                href={`/enquetes/${row.id}/participations`}
                onClick={(event) => {
                    if (
                        event.button !== 0
                        || event.metaKey
                        || event.ctrlKey
                        || event.shiftKey
                        || event.altKey
                    ) {
                        return;
                    }
                    event.preventDefault();
                    navigate(`/enquetes/${row.id}/participations`);
                }}
            />
        ),
        toggleable: false,
    }
];

const Surveys = () => {
    const navigate = useNavigate();
    const [surveys, setSurveys] = useState([]);
    const {showLoader, hideLoader} = useLoader();
    const [filterValues, setFilterValues] = useState(() => buildDefaultFilters());

    function fetchSurveys(filters = filterValues) {
        showLoader();
        const statuses = filters?.statusList || [];
        const surveysRequest = statuses?.length
            ? onlineProcessesService.getSurveys(statuses)
            : onlineProcessesService.getSurveys();

        surveysRequest
            .then((response) => {
                setSurveys(sortSurveysByStatus(response || []));
            })
            .catch(err => {
                console.error(err);
            })
            .finally(() => {
                hideLoader();
            });
    }

    useEffect(() => {
        fetchSurveys(buildDefaultFilters());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilterChange = (fieldId, value) => {
        setFilterValues((prev) => ({...prev, [fieldId]: value}));
    };

    const handleApply = (values) => {
        setFilterValues(values);
        fetchSurveys(values);
    };

    const handleReset = () => {
        const emptyFilters = {statusList: []};
        setFilterValues(emptyFilters);
        fetchSurveys(emptyFilters, false);
    };

    function handleStatusSort(columnId, order) {
        if (columnId !== 'statusRank') return;
        const nextDirection = order === 'asc' ? 'desc' : 'asc';
        setSurveys((previousSurveys) => sortSurveysByStatus(previousSurveys, nextDirection));
    }

    return (
        <div className="page-content">
            <QuadralTypography variant="modalTitle" gutterBottom> Enquêtes</QuadralTypography>
            <QuadralTypography variant="label">Recherche par statut</QuadralTypography>
            <AdvancedFilters
                fields={FILTER_FIELDS}
                values={filterValues}
                onChange={handleFilterChange}
                onApply={handleApply}
                onReset={handleReset}
            />
            <QuadralList
                columns={buildColumns(navigate)}
                rows={surveys}
                sortMethod={handleStatusSort}
            />
        </div>
    );
};

export default Surveys;